import { useEffect, useState } from "react";
import { DatePicker, Textarea } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { Button, StaticSelect, CustomModal, FormError, showToast } from "../shared";
import GradientSelect from "./GradientSelect";
import { extractBackendFieldErrors, toIsoDate, toUsDate } from "../../utils/utils";
import { useCreateProject } from "../../lib/react-query/projects";
import { STATUS_OPTIONS } from "../../constants/statuses";
import { cn } from "../../lib/cn";

const CreateProjectModal = ({ isOpen, onClose }) => {
  const { mutateAsync: createProject, isLoading: creating } = useCreateProject();
  const [state, setState] = useState({ loading: false, error: null, fieldErrors: {} });
  const [form, setForm] = useState({ title: "", description: "", status: "PENDING", start_date: "", end_date: "", header_gradient: "gradient-1" });
  const startIso = toIsoDate(form.start_date);
  const endIso = toIsoDate(form.end_date);

  useEffect(() => {
    if (!isOpen) {
      setState({ loading: false, error: null, fieldErrors: {} });
      setForm({ title: "", description: "", status: "PENDING", start_date: "", end_date: "", header_gradient: "gradient-1" });
    }
  }, [isOpen]);

  const validate = () => {
    const errors = {};
    if (!form.title || !String(form.title).trim()) {
      errors.title = "Title is required";
    }
    if (form.start_date && form.end_date) {
      const s = new Date(form.start_date);
      const e = new Date(form.end_date);
      if (isFinite(s) && isFinite(e) && s > e) {
        errors.dates = "Start date must be before or equal to end date";
      }
    }
    return Object.keys(errors).length > 0 ? errors : null;
  };

  const clearFieldError = (field) => {
    if (state.fieldErrors?.[field]) {
      setState((p) => ({ ...p, fieldErrors: { ...p.fieldErrors, [field]: null } }));
    }
  };

  const handleCreate = async () => {
    const fieldErrors = validate();
    if (fieldErrors) {
      setState((p) => ({ ...p, fieldErrors, error: null }));
      return;
    }

    try {
      setState({ loading: true, error: null, fieldErrors: {} });
      const payload = {
        title: form.title,
        description: form.description,
        status: form.status,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        header_gradient: form.header_gradient,
      };
      await createProject(payload);
      showToast("Project created successfully", "success", { position: "top-center" });
      onClose?.();
    } catch (err) {
      const { fieldErrors: backendErrors, generalError } = extractBackendFieldErrors(err);
      setState({
        loading: false,
        error: generalError,
        fieldErrors: backendErrors,
      });
      return;
    }
  };

  return (
    <CustomModal
      title="Create Project"
      open={isOpen}
      close={onClose}
      primaryButtonText="Create"
      primaryButtonClick={handleCreate}
      primaryButtonLoading={state.loading || creating}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
    >
      <div className="flex flex-col gap-4">
        {/* Title */}
        <div>
          <label htmlFor="create-project-title" className="block text-sm font-sans font-medium text-grey mb-1.5">
            Title <span className="text-danger">*</span>
          </label>
          <input
            id="create-project-title"
            type="text"
            name="title"
            value={form.title}
            onChange={(e) => {
              setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
              clearFieldError("title");
            }}
            placeholder="Enter project title"
            className={cn(
              "w-full h-12 px-3 text-sm bg-lightgrey border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors",
              state.fieldErrors?.title ? "border-danger/40 focus:border-danger" : "border-surface-border focus:border-primary"
            )}
          />
          {state.fieldErrors?.title && (
            <p className="mt-1 text-xs text-danger">{state.fieldErrors.title}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="create-project-description" className="block text-sm font-sans font-medium text-grey mb-1.5">
            Description
          </label>
          <Textarea
            id="create-project-description"
            name="description"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }))}
            placeholder="Describe your project..."
            minRows={3}
            maxRows={6}
            classNames={{
              inputWrapper: "shadow-none rounded-md border border-surface-border",
              input: "text-sm font-sans",
              label: "font-sans text-grey",
            }}
          />
        </div>

        {/* Status and Gradient Row */}
        <div className="grid grid-cols-2 gap-3">
          <StaticSelect
            options={STATUS_OPTIONS}
            label="Status"
            name="status"
            value={form.status}
            onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
          />
          <GradientSelect
            value={form.header_gradient}
            onChange={(e) => setForm((p) => ({ ...p, header_gradient: e.target.value }))}
            label="Theme"
          />
        </div>

        {/* Dates Row */}
        <I18nProvider locale="en-US">
          <div className="grid grid-cols-2 gap-3">
            <DatePicker
              label="Start Date"
              disableAnimation
              value={startIso ? parseDate(startIso) : null}
              onChange={(date) => {
                setForm((p) => ({ ...p, start_date: date ? toUsDate(date.toString()) : "" }));
                clearFieldError("dates");
              }}
              showMonthAndYearPickers
              classNames={{
                inputWrapper: "h-[48px] shadow-none rounded-md border border-surface-border",
                label: "font-sans text-grey",
              }}
            />
            <DatePicker
              label="End Date"
              disableAnimation
              value={endIso ? parseDate(endIso) : null}
              onChange={(date) => {
                setForm((p) => ({ ...p, end_date: date ? toUsDate(date.toString()) : "" }));
                clearFieldError("dates");
              }}
              minValue={startIso ? parseDate(startIso) : undefined}
              showMonthAndYearPickers
              classNames={{
                inputWrapper: "h-[48px] shadow-none rounded-md border border-surface-border",
                label: "font-sans text-grey",
              }}
            />
          </div>
        </I18nProvider>

        {/* Form Error */}
        <FormError message={state.error || state.fieldErrors?.dates} />

        <Button className="hidden" disabled={state.loading || creating} />
      </div>
    </CustomModal>
  );
};

export default CreateProjectModal;
